import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import SlideOver from "../../../components/slideOver/SlideOver";
import TaxRateForm from "../../../components/forms/taxRates/TaxRateForm";
import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";
import type { ApiErrorPayload, ApiMessage } from "../../../types/api";
import type {
  TaxRate,
  TaxRateListResponse,
  TaxRateMutationResponse,
  TaxRatePayload,
} from "../../../types/taxRates";

interface SlideOverState {
  childComponent: ReactNode;
  open: boolean;
}

interface TaxRateFormValues {
  name: string;
  rate: string;
  active: boolean;
}

const closedSlideOver: SlideOverState = { childComponent: null, open: false };
const formatRate = (value: string | number) =>
  Number(value).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");

const apiErrorMessage = (error: ApiErrorPayload, fallback: string) =>
  error.message ?? Object.values(error.errors ?? {}).flat()[0] ?? fallback;

export default function TaxRates() {
  const { showSuccess } = useToast();
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [slideOver, setSlideOver] = useState<SlideOverState>(closedSlideOver);

  const loadRates = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await makeGetApiCall<TaxRateListResponse>("tax-rates");
    if (result.success) setRates(result.data.data);
    else setError(result.data?.message || "Could not load tax rates.");
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadRates();
  }, [loadRates]);

  const filteredRates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rates;
    return rates.filter((rate) =>
      `${rate.name} ${formatRate(rate.rate)} ${rate.active ? "active" : "inactive"}`
        .toLowerCase()
        .includes(term)
    );
  }, [rates, search]);

  const closePanel = () => setSlideOver(closedSlideOver);

  const saveRate = async (values: TaxRateFormValues, id?: number) => {
    const payload: TaxRatePayload = {
      name: values.name.trim(),
      rate: Number(values.rate),
      active: Boolean(values.active),
    };
    const result = id
      ? await makePutApiCall<TaxRateMutationResponse, TaxRatePayload>(`tax-rates/${id}`, payload)
      : await makePostApiCall<TaxRateMutationResponse, TaxRatePayload>("tax-rates", payload);
    if (!result.success) {
      return {
        success: false,
        message: apiErrorMessage(result.data, "Could not save tax rate."),
      };
    }
    showSuccess(result.data.message);
    closePanel();
    await loadRates();
    return { success: true };
  };

  const openPanel = (rate: TaxRate | null = null) => {
    setSlideOver({
      childComponent: <TaxRateForm taxRate={rate} onCancel={closePanel} onSave={saveRate} />,
      open: true,
    });
  };

  const removeRate = async (rate: TaxRate) => {
    if (!window.confirm(`Delete ${rate.name}? Existing order items will not change.`)) return;
    const result = await makeDeleteApiCall<ApiMessage>(`tax-rates/${rate.id}`);
    if (result.success) {
      showSuccess(result.data.message);
      await loadRates();
    } else setError(result.data?.message || "Could not delete tax rate.");
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">Tax rates</h1>
              <p className="mt-1 text-sm text-gray-500">
                Rates assigned to customers become the default on taxable order items.
              </p>
              <div className="mt-5 flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                  <input
                    type="search"
                    className="block w-full rounded-lg rounded-l-md rounded-tr-none rounded-br-none border border-gray-300 pl-2 sm:text-sm"
                    placeholder="Search by tax rate name"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700 hover:bg-gray-100"
                  aria-label="Search tax rates"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => openPanel()}
                  className="ml-5 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                >
                  Add tax rate
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
              {error}
            </p>
          )}

          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <div className="h-10 w-full rounded-md border p-4 shadow">
                    <div className="flex animate-pulse">Loading</div>
                  </div>
                ) : (
                  <div className="overflow-hidden card-custom">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-white">
                        <tr>
                          <th className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Name
                          </th>
                          <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                            Rate
                          </th>
                          <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Status
                          </th>
                          <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {filteredRates.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-6 py-10 text-center text-sm text-gray-500"
                            >
                              {rates.length === 0
                                ? "No tax rates configured."
                                : "No tax rates match your search."}
                            </td>
                          </tr>
                        ) : (
                          filteredRates.map((rate) => (
                            <tr key={rate.id}>
                              <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                {rate.name}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-right font-mono text-sm tabular-nums text-gray-700">
                                {formatRate(rate.rate)}%
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {rate.active ? "Active" : "Inactive"}
                              </td>
                              <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <button
                                  type="button"
                                  onClick={() => openPanel(rate)}
                                  className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeRate(rate)}
                                  className="ml-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SlideOver
        childComponent={slideOver.childComponent}
        customClass="max-w-md"
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}
